/**
 * @file 見取り図タブの「さんすうの進め方」カード。
 *
 * @what: 『2才児のさんすう』の 7 つの段を順に並べ、段ごとのねらい・やること・本のページを出す。
 *   本だなに資料があるときは、段や遊びから該当ページを直接開ける。
 * @why: 本は解説と教具の型紙が交互に並ぶ作りで、通して読まないと順序が掴めない
 *   (代表 2026-08-25)。「いつ何をやるか」だけを抜き出して、開くページに直行できるようにする。
 * @failure: 描画のみ。sources が null (読込前・読込失敗) または資料が未登録のときは
 *   リンクを出さず、文言とページ番号だけで成立させる。※なぜなら連番へ直すには資料の
 *   reader_offset が必要で、それが読めないうちは開く先を決められないためである。
 */

import { Link } from "react-router-dom";
import type { StudySource } from "../api/sources";
import { SANSUU_FLOW, SANSUU_PRINCIPLES, SANSUU_SOURCE_ID } from "../model/sansuuFlow";
import { readerPageOf } from "../model/sourcePages";
import "./study.css";

export function SansuuFlowCard({
  sources,
  sourceHrefBase,
}: {
  /** 本だなの資料 (ページリンクの出し分けに使う。未読込は null)。 */
  sources: readonly StudySource[] | null;
  sourceHrefBase: string;
}) {
  const book = sources?.find((s) => s.id === SANSUU_SOURCE_ID) ?? null;
  // カタログのページは紙に刷られた番号なので、開く先はその資料の差を足した連番にする。
  // ※なぜならリーダーが受け取る `?page=` は連番で、この本は巻頭に番号のない面が4枚あるためである。
  const pageLink = (page: number, label: string) =>
    book !== null ? (
      <Link
        className="study-sansuu-page"
        to={`${sourceHrefBase}/${encodeURIComponent(SANSUU_SOURCE_ID)}?page=${readerPageOf(book, page)}`}
        title="本だなで、このページを開く"
      >
        {label}
      </Link>
    ) : (
      <span className="study-sansuu-page study-sansuu-page--plain">{label}</span>
    );

  return (
    <section className="study-section">
      <h2 className="study-section-title">
        <span aria-hidden="true">🔢</span> さんすうの進め方
      </h2>
      <p className="study-section-help">
        『2才児のさんすう』(伊藤恭) の流れです。上から順に進み、前の段が確実になってから次へ移ります。
        数えさせるのではなく、ぱっと見ていくつか分かるように遊ぶ、が一貫した考え方です。
      </p>

      <details className="study-howto" open>
        <summary>始める前に</summary>
        <ul className="study-sansuu-principles">
          {SANSUU_PRINCIPLES.map((p) => (
            <li key={p.label}>
              <strong>{p.label}</strong>
              <span>{p.note}</span>
              {pageLink(p.page, `p.${p.page}`)}
            </li>
          ))}
        </ul>
      </details>

      <ol className="study-sansuu-steps">
        {SANSUU_FLOW.map((step, i) => (
          <li key={step.id} className="study-sansuu-step">
            <div className="study-sansuu-step-head">
              <span className="study-sansuu-step-no" aria-hidden="true">
                {i + 1}
              </span>
              <div className="study-sansuu-step-title">
                <strong>{step.label}</strong>
                <span className="study-sansuu-step-meta">
                  {step.chapter}
                  {pageLink(step.page, `p.${step.page}`)}
                </span>
              </div>
            </div>
            <p className="study-sansuu-goal">{step.goal}</p>
            {step.timing ? <p className="study-sansuu-timing">目安: {step.timing}</p> : null}
            <ul className="study-sansuu-activities">
              {step.activities.map((a) => (
                <li key={a.label}>
                  <span>{a.label}</span>
                  {a.page !== undefined ? pageLink(a.page, `p.${a.page}`) : null}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ol>
    </section>
  );
}
